import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { NAV_ITEMS, isExternal } from '../nav/routes';
import { internalHref } from '../offers/links';
import type { Lang } from '../offers/config';
const PRIMARY_CTA = '/oferta/credito';
function NavItem({
  label,
  path,
  className,
  onClick
}: {
  label: string;
  path: string;
  className: string;
  onClick?: () => void;
}) {
  if (isExternal(path)) {
    return <a href={path} className={className} onClick={onClick} data-id="element-234" data-mp="ek763eiuuksw">
        {label}
      </a>;
  }
  return <Link to={path} className={className} onClick={onClick} data-id="element-235" data-mp="9iq8pszkxgh2">
      {label}
    </Link>;
}
export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const {
    language,
    setLanguage,
    t
  } = useLanguage();
  const {
    search
  } = useLocation();
  const lang = (language === 'en' ? 'en' : 'es') as Lang;
  const reducedMotion = useReducedMotion();
  const lastScrollY = useRef(0);

  // Toda navegacion interna conserva los parametros de la campana y se
  // traduce al idioma activo (/oferta/credito <-> /offer/credit).
  const href = (path: string) => isExternal(path) ? path : internalHref(path, lang, search);
  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 20);
      if (mobileMenuOpen || y < 80) {
        setHidden(false);
      } else if (y > lastScrollY.current + 6) {
        setHidden(true);
      } else if (y < lastScrollY.current - 6) {
        setHidden(false);
      }
      lastScrollY.current = y;
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [mobileMenuOpen]);
  useEffect(() => {
    if (mobileMenuOpen) setHidden(false);
  }, [mobileMenuOpen]);
  useEffect(() => {
    const footer = document.querySelector('footer');
    if (!footer) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !mobileMenuOpen) setHidden(true);
    }, {
      threshold: 0.01
    });
    observer.observe(footer);
    return () => observer.disconnect();
  }, [mobileMenuOpen]);
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);
  return <motion.header initial={{
    y: -100
  }} animate={{
    y: hidden ? -120 : 0
  }} transition={reducedMotion ? {
    duration: 0
  } : {
    duration: 0.24,
    ease: [0.16, 1, 0.3, 1]
  }} className={`fixed top-0 left-0 right-0 z-40 transition-[padding] duration-300 ${scrolled ? 'py-2.5 md:py-4' : 'py-3 md:py-6'}`} data-id="element-236" data-mp="2sc69t93eaelw">
      <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-237" data-mp="a4950y8d3d3s">
        <div className={`glass-panel rounded-full pl-4 pr-3 sm:px-6 flex items-center justify-between transition-colors duration-300 h-[60px] md:h-auto md:py-3 ${scrolled ? 'bg-surface/80' : 'bg-surface/40'}`} data-id="element-238" data-mp="a6vdr16znynl">
          <Link to={href('/')} aria-label="Blinkblis" className="inline-flex items-center gap-2.5 sm:gap-3 bg-transparent p-0 border-0 shadow-none cursor-pointer group shrink-0 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60" data-id="element-239" data-mp="55fr54p5xk33">
            <img src="https://cdn.magicpatterns.com/uploads/w8wf8HrNFmFRNJWrke4AoT/blinkblis-navbar-mark-transparent-512.png" alt="Blinkblis" className="block h-11 w-11 md:h-12 md:w-12 shrink-0 object-contain bg-transparent p-0 m-0 border-0 shadow-none rounded-none" style={{
            background: 'transparent',
            padding: 0,
            border: 0,
            boxShadow: 'none',
            filter: 'none',
            borderRadius: 0
          }} data-id="element-240" data-mp="k3hwnkfr8fmz" />
            <span className="block text-white text-[22px] sm:text-[22px] font-semibold tracking-[-0.02em] leading-none group-hover:text-white/90 transition-colors" data-id="element-241" data-mp="3k4x930d39zlp">
              Blinkblis
            </span>
          </Link>

          {/* Desktop Nav — solo destinos que existen */}
          <nav className="hidden md:flex items-center gap-7 mx-6" data-id="element-242" data-mp="bmitu5draob4">
            {NAV_ITEMS.map(item => <NavItem key={item.path} label={item.label[lang]} path={href(item.path)} className="text-sm text-white/70 hover:text-white transition-colors whitespace-nowrap rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60" data-id="element-243" data-mp="6opp7gs3q2cy" />)}
          </nav>

          {/* Right Actions */}
          <div className="hidden md:flex items-center gap-4 shrink-0" data-id="element-244" data-mp="plimrp8axpd3">
            <div className="flex items-center gap-1 text-xs font-medium text-white/50 bg-white/5 p-1 rounded-full border border-white/10" data-id="element-245" data-mp="28aldk4fgtcfj">
              <button onClick={() => setLanguage('en')} className={`px-2.5 py-1 rounded-full transition-all duration-300 active:scale-[0.95] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 ${language === 'en' ? 'bg-white/10 text-white shadow-[0_2px_8px_rgba(0,0,0,0.2)]' : 'hover:text-white hover:bg-white/5'}`} data-id="element-246" data-mp="2dahl2dndflzu">
                EN
              </button>
              <button onClick={() => setLanguage('es')} className={`px-2.5 py-1 rounded-full transition-all duration-300 active:scale-[0.95] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 ${language === 'es' ? 'bg-white/10 text-white shadow-[0_2px_8px_rgba(0,0,0,0.2)]' : 'hover:text-white hover:bg-white/5'}`} data-id="element-247" data-mp="30yhbtdkoncef">
                ES
              </button>
            </div>
            <Link to={href(PRIMARY_CTA)} className="bg-white text-background px-5 py-2.5 rounded-full text-sm font-medium hover:bg-white/90 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(255,255,255,0.1)] active:scale-[0.98] transition-all duration-300 whitespace-nowrap shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60" data-id="element-248" data-mp="1ewitgv6vsshp">
              {t('nav.startExploring')}
            </Link>
          </div>

          {/* Mobile Toggle */}
          <button className="md:hidden text-white/80 hover:text-white w-11 h-11 flex items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 active:bg-white/5 transition-colors" aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileMenuOpen} onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-id="element-249" data-mp="1tk3tuhcdgdlx">
            {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-250" data-mp="2ju4bf8t4pwns" /> : <Menu className="w-6 h-6" data-id="element-251" data-mp="2jwqp7q6m58eg" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-252" data-mp="2a547dazrsb2q">
        {mobileMenuOpen && <motion.div initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={reducedMotion ? {
        duration: 0
      } : {
        duration: 0.22
      }} className="md:hidden absolute top-full left-0 right-0 mt-2 px-4 max-h-[calc(100dvh-90px)] overflow-y-auto" data-id="element-253" data-mp="2a4b6gqnhsfsi">
            <div className="relative rounded-3xl p-6 flex flex-col gap-6 bg-background/85 backdrop-blur-[14px] border border-white/10 overflow-hidden" data-id="element-254" data-mp="1r6i9e4zjv031">
              <div className="absolute -top-16 -right-10 w-48 h-48 bg-accent/10 rounded-full blur-3xl pointer-events-none" data-id="element-255" data-mp="1w75vcc8x8qqe" />

              <nav className="relative flex flex-col gap-7" data-id="element-256" data-mp="3qiudenag99f0">
                {NAV_ITEMS.map(item => <NavItem key={item.path} label={item.label[lang]} path={href(item.path)} onClick={() => setMobileMenuOpen(false)} className="text-[23px] font-display font-medium text-white/85 hover:text-white transition-colors leading-none min-h-[44px] flex items-center rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60" data-id="element-257" data-mp="2e9hc9s1bwj70" />)}
              </nav>

              <div className="relative h-px bg-white/10" data-id="element-258" data-mp="1qjwuvdktptzu" />

              <div className="relative flex flex-col gap-5" data-id="element-259" data-mp="1qmj2tebmmbil">
                <div className="flex items-center gap-1 text-sm font-medium text-white/50 bg-white/5 p-1 rounded-full border border-white/10 w-fit" data-id="element-260" data-mp="1llv2g21icibp">
                  <button onClick={() => setLanguage('en')} className={`px-5 py-2 rounded-full transition-all duration-300 active:scale-[0.95] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 ${language === 'en' ? 'bg-white/10 text-white shadow-[0_2px_8px_rgba(0,0,0,0.2)]' : 'hover:text-white hover:bg-white/5'}`} data-id="element-261" data-mp="3fz2xnuteavxv">
                    EN
                  </button>
                  <button onClick={() => setLanguage('es')} className={`px-5 py-2 rounded-full transition-all duration-300 active:scale-[0.95] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 ${language === 'es' ? 'bg-white/10 text-white shadow-[0_2px_8px_rgba(0,0,0,0.2)]' : 'hover:text-white hover:bg-white/5'}`} data-id="element-262" data-mp="3sana2zrxuwjp">
                    ES
                  </button>
                </div>
                <Link to={href(PRIMARY_CTA)} onClick={() => setMobileMenuOpen(false)} className="w-full inline-flex items-center justify-center bg-white text-background px-5 h-[52px] rounded-full text-[16px] font-medium active:scale-[0.98] transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60" data-id="element-263" data-mp="3ehns8r4ooq8z">
                  {t('nav.startExploring')}
                </Link>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.header>;
}